import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, DollarSign, Cog, CheckSquare, Clock, Search, RefreshCw, CalendarIcon, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import ReactApexChart from 'react-apexcharts';
import api from '../../utils/api';

interface Order {
  id: number;
  client_id: number;
  vehicle_id: number;
  service_id: number;
  technicien_id: number | null;
  status: string;
  created_at: string;
  scheduled_at: string;
  client: {
    first_name: string;
    last_name: string;
  };
  company: {
    raison_social: string;
  };
  vehicle: {
    plate_number: string;
    brand: {
      name: string;
    };
    model: string;
    year: string;
  };
  service: {
    name: string;
    base_price: number;
  };
  final_price: number | null;
}

interface OrderStats {
  total: number;
  pending: number;
  in_progress: number;
  completed: number;
  cancelled: number;
  total_revenue: number;
}

interface PaginationData {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
}

export default function OrdersList() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [pagination, setPagination] = useState<PaginationData>({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
    from: 0,
    to: 0
  });
  const [toast, setToast] = useState({ visible: false, severity: '', message: '' });
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchOrders();
  }, [statusFilter, dateRange, globalFilter, pagination.current_page]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/service-orders', {
        params: {
          status: statusFilter,
          start_date: dateRange[0]?.toISOString().split('T')[0],
          end_date: dateRange[1]?.toISOString().split('T')[0],
          search: globalFilter,
          page: pagination.current_page,
          per_page: pagination.per_page
        }
      });
      setOrders(response.data.data);
      setStats(response.data.stats);
      setPagination({
        current_page: response.data.current_page,
        last_page: response.data.last_page,
        per_page: response.data.per_page,
        total: response.data.total,
        from: response.data.from,
        to: response.data.to
      });
    } catch (error) {
      console.error('Error fetching orders:', error);
      showToast('error', t('error_fetching_orders'));
    } finally {
      setLoading(false);
    }
  };

  const showToast = (severity: string, message: string) => {
    setToast({ visible: true, severity, message });
    setTimeout(() => setToast({ ...toast, visible: false }), 3000);
  };

  const statusOptions = [
    { label: t('all_statuses'), value: null },
    { label: t('pending'), value: 'pending' },
    { label: t('in_progress'), value: 'in_progress' },
    { label: t('completed'), value: 'completed' },
    { label: t('cancelled'), value: 'cancelled' },
  ];

  const resetFilters = () => {
    setStatusFilter(null);
    setDateRange([null, null]);
    setGlobalFilter('');
    setPagination(prev => ({ ...prev, current_page: 1 }));
    fetchOrders();
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, current_page: newPage }));
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const totalPages = pagination.last_page;
    const currentPage = pagination.current_page;

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        buttons.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={`px-3 py-1 rounded ${
              currentPage === i ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            {i}
          </button>
        );
      }
    } else {
      buttons.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className={`px-3 py-1 rounded ${
            currentPage === 1 ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
          }`}
        >
          1
        </button>
      );

      if (currentPage > 3) {
        buttons.push(<span key="ellipsis1" className="px-3 py-1">...</span>);
      }

      for (let i = Math.max(2, currentPage - 1); i <= Math.min(currentPage + 1, totalPages - 1); i++) {
        buttons.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={`px-3 py-1 rounded ${
              currentPage === i ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            {i}
          </button>
        );
      }

      if (currentPage < totalPages - 2) {
        buttons.push(<span key="ellipsis2" className="px-3 py-1">...</span>);
      }

      buttons.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className={`px-3 py-1 rounded ${
            currentPage === totalPages ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
          }`}
        >
          {totalPages}
        </button>
      );
    }

    return buttons;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {toast.visible && (
        <div className={`fixed top-4 right-4 p-4 rounded-md shadow-md ${
          toast.severity === 'error' ? 'bg-red-500' : 'bg-green-500'
        } text-white`}>
          {toast.message}
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">{t('orders_list')}</h2>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder={t('search')}
            className="p-2 border rounded-md"
          />
          <select
            value={statusFilter || ''}
            onChange={(e) => setStatusFilter(e.target.value || null)}
            className="p-2 border rounded-md"
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value || ''}>{option.label}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateRange[0]?.toISOString().split('T')[0] || ''}
            onChange={(e) => {
              const selectedDate = new Date(e.target.value);
              const nextDay = new Date(selectedDate);
              nextDay.setDate(selectedDate.getDate() + 1);
              setDateRange([selectedDate, dateRange[1] ? dateRange[1] : nextDay]);
            }}
            className="p-2 border rounded-md"
          />
          <input
            type="date"
            value={dateRange[1]?.toISOString().split('T')[0] || ''}
            onChange={(e) => {
              const selectedDate = new Date(e.target.value);
              const nextDay = new Date(selectedDate);
              nextDay.setDate(selectedDate.getDate() - 1);
              setDateRange([ dateRange[0] ? dateRange[0] : nextDay,selectedDate]);
            }}
            className="p-2 border rounded-md"
          />
 {/*          <button
            onClick={fetchOrders}
            className="p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            <Filter className="w-5 h-5" />
          </button> */}
          <button
            onClick={resetFilters}
            className="p-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr className="bg-gray-200 text-gray-600 uppercase text-sm leading-normal">
                <th className="py-3 px-6 text-left">{t('date')}</th>
                <th className="py-3 px-6 text-left">{t('client')}</th>
                <th className="py-3 px-6 text-left">{t('vehicle')}</th>
                <th className="py-3 px-6 text-left">{t('service')}</th>
                <th className="py-3 px-6 text-left">{t('price')}</th>
                <th className="py-3 px-6 text-left">{t('status')}</th>
                <th className="py-3 px-6 text-center">{t('actions')}</th>
              </tr>
            </thead>
            <tbody className="text-gray-600 text-sm font-light">
              {orders.map((order) => (
                <tr key={order.id} className="border-b border-gray-200 hover:bg-gray-100">
                  <td className="py-3 px-6 text-left">
                    <div>{new Date(order.scheduled_at).toLocaleDateString()}</div>
                    <small className='text-gray-400 d-block text-xxs'>{t('created_at')}: {new Date(order.created_at).toLocaleDateString()}</small>
                  </td>
                  <td className="py-3 px-6 text-left">{`${order.company.raison_social}`}</td>
                  <td className="py-3 px-6 text-left text-center">
                    <div>{`${order.vehicle.brand.name} ${order.vehicle.model}`}</div>
                    <div className="text-xs text-gray-500">{order.vehicle.plate_number}</div>
                  </td>
                  <td className="py-3 px-6 text-left">{order?.service?.name || t("deleted")}</td>
                  <td className="py-3 px-6 text-left">{`${order.final_price} MAD`}</td>
                  <td className="py-3 px-6 text-center min-w-[150px]">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      order.status === 'completed' ? 'bg-green-200 text-green-800' :
                      order.status === 'in_progress' ? 'bg-blue-200 text-blue-800' :
                      order.status === 'cancelled' ? 'bg-red-200 text-red-800' :
                      'bg-yellow-200 text-yellow-800'
                    }`}>
                      {t(order.status)}
                    </span>
                  </td>
                  <td className="py-3 px-6 text-center">
                    <button
                      onClick={() => navigate(`/orders/${order.id}`)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-between items-center mt-4">
        <div>
          {t('showing')} {pagination.from} - {pagination.to} {t('of')} {pagination.total}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(pagination.current_page - 1)}
            disabled={pagination.current_page === 1}
            className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          {renderPaginationButtons()}
          <button
            onClick={() => handlePageChange(pagination.current_page + 1)}
            disabled={pagination.current_page === pagination.last_page}
            className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

